import React from 'react';

const RadarChart = ({ data, size = 400 }) => {
  const center = size / 2;
  const radius = center - 60;
  const maxScore = 5;

  // Extract categories dynamically from the data
  const categories = Object.keys(data || {})
    .filter(key => data[key] && typeof data[key] === 'object' && 'name' in data[key])
    .map(key => data[key].name);

  const getPointCoordinates = (angle, value) => {
    const radian = (angle - 90) * (Math.PI / 180);
    const adjustedRadius = (value / maxScore) * radius;
    return {
      x: center + adjustedRadius * Math.cos(radian),
      y: center + adjustedRadius * Math.sin(radian)
    };
  };

  const getLabelCoordinates = (angle, offset = 30) => {
    const radian = (angle - 90) * (Math.PI / 180);
    const labelRadius = radius + offset;
    return {
      x: center + labelRadius * Math.cos(radian),
      y: center + labelRadius * Math.sin(radian)
    };
  };

  const createGridLevels = () => {
    const levels = [];
    for (let i = 1; i <= maxScore; i++) {
      const points = categories.map((_, index) => {
        const angle = (360 / categories.length) * index;
        return getPointCoordinates(angle, i);
      });
      
      const pathData = points.map((point, index) => 
        `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
      ).join(' ') + ' Z';
      
      levels.push(
        <path
          key={i}
          d={pathData}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="1"
          opacity={0.5}
        />
      );
    }
    return levels;
  };

  const createGridLines = () => {
    return categories.map((_, index) => {
      const angle = (360 / categories.length) * index;
      const endPoint = getPointCoordinates(angle, maxScore);
      
      return (
        <line
          key={index}
          x1={center}
          y1={center}
          x2={endPoint.x}
          y2={endPoint.y}
          stroke="#e0e0e0"
          strokeWidth="1"
          opacity={0.5}
        />
      );
    });
  };

  const createDataPath = () => {
    const points = categories.map((category, index) => {
      const angle = (360 / categories.length) * index;
      const categoryKey = Object.keys(data).find(key => 
        data[key] && typeof data[key] === 'object' && 'name' in data[key] && 
        data[key].name === category
      );
      const score = categoryKey ? (data[categoryKey].score || 0) : 0;
      return getPointCoordinates(angle, score);
    });
    
    const pathData = points.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ') + ' Z';
    
    return (
      <path
        d={pathData}
        fill="rgba(220, 0, 50, 0.2)"
        stroke="#DC0032"
        strokeWidth="2"
      />
    );
  };

  const createDataPoints = () => {
    return categories.map((category, index) => {
      const angle = (360 / categories.length) * index;
      const categoryKey = Object.keys(data).find(key => 
        data[key] && typeof data[key] === 'object' && 'name' in data[key] && 
        data[key].name === category
      );
      const score = categoryKey ? (data[categoryKey].score || 0) : 0;
      const point = getPointCoordinates(angle, score);
      
      return (
        <circle
          key={index}
          cx={point.x}
          cy={point.y}
          r="4"
          fill="#DC0032"
          stroke="white"
          strokeWidth="2"
        />
      );
    });
  };

  const createLabels = () => {
    return categories.map((category, index) => {
      const angle = (360 / categories.length) * index;
      const categoryKey = Object.keys(data).find(key => 
        data[key] && typeof data[key] === 'object' && 'name' in data[key] && 
        data[key].name === category
      );
      const score = categoryKey ? (data[categoryKey].score || 0) : 0;
      const labelPos = getLabelCoordinates(angle);
      
      const textAnchor = labelPos.x > center ? 'start' : labelPos.x < center ? 'end' : 'middle';
      const dy = labelPos.y > center ? '0.3em' : labelPos.y < center ? '-0.3em' : '0.3em';
      
      return (
        <g key={index}>
          <text
            x={labelPos.x}
            y={labelPos.y}
            textAnchor={textAnchor}
            dy={dy}
            fontSize="12"
            fill="#2D2D2D"
            fontWeight="500"
          >
            {category}
          </text>
          <text
            x={labelPos.x}
            y={labelPos.y + (labelPos.y > center ? 15 : -15)}
            textAnchor={textAnchor}
            fontSize="11"
            fill="#666"
          >
            {score.toFixed(1)}/5
          </text>
        </g>
      );
    });
  };

  const createScoreLabels = () => {
    const labels = [];
    for (let i = 1; i <= maxScore; i++) {
      const point = getPointCoordinates(0, i);
      labels.push(
        <text
          key={i}
          x={point.x + 8}
          y={point.y}
          fontSize="10"
          fill="#999"
          textAnchor="start"
          dy="0.3em"
        >
          {i}
        </text>
      );
    }
    return labels;
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
      <svg width={size} height={size} style={{ overflow: 'visible' }}>
        {createGridLevels()}
        {createGridLines()}
        {createScoreLabels()}
        {createDataPath()}
        {createDataPoints()}
        {createLabels()}
      </svg>
    </div>
  );
};

export default RadarChart;